/**
 * Coach Engine — Observation Archive
 *
 * Pure query/aggregation helpers over the observation corpus produced by
 * the coach. The "archive" is a DERIVED view: `session.aiInsight.observations`
 * on each session is the source of truth, and we persist those alongside
 * the session itself. No duplicate storage, no sync hazards, and the
 * archive works retroactively on every session you already journaled.
 *
 * These helpers power the progress scope (2B-4), the Report Card UI
 * (2B-5), end-of-day/week rollups (2B-6), and per-session recurrence
 * badges (2B-7). They are intentionally small and composable so higher
 * layers can chain filters without re-walking the corpus more than once.
 *
 * All functions are pure and side-effect-free. No React, no DOM, no IPC.
 *
 * Typical usage:
 *
 *   import {
 *     queryObservations, countByAnalyzer, recurrenceInfo
 *   } from 'services/coach';
 *
 *   // "How many times did revengeSizing fire in the last 30 days?"
 *   const hits = queryObservations({
 *     sessions, playbooks, windowDays: 30, analyzer: 'revengeSizing'
 *   });
 *   hits.length; // → 7
 *
 *   // "Is that trending up or down vs the prior 30 days?"
 *   const info = recurrenceInfo(
 *     queryObservations({ sessions, playbooks, analyzer: 'revengeSizing' }),
 *     'revengeSizing'
 *   );
 *   info.trend; // → 'up' | 'down' | 'flat' | 'first-time'
 */

/**
 * @typedef {Object} ArchivedObservation
 * An Observation enriched with the session metadata the analyzer didn't
 * know at generation time. The shape extends Observation with:
 *
 *   sessionId:    string       The owning session
 *   sessionDate:  string|null  'YYYY-MM-DD' — for time-window queries
 *   sessionName:  string|null  Human-readable session title
 *   playbookId:   string|null  Strategic intent tag (null if unassigned)
 *   playbookName: string|null  Resolved name, if the playbook still exists
 *   generatedAt:  string|null  When the hosting aiInsight was computed
 */

// ────────────────────────────────────────────────────────────────────────────
// Date helpers — sourced from the central timeService. Sessions already store
// session.date as 'YYYY-MM-DD' (the authoritative trading-day key), so all
// arithmetic is string-in / string-out and dodges timezone hazards.
// ────────────────────────────────────────────────────────────────────────────

import { getCurrentTradingDay, addDaysToDateStr, weekStartOfDateStr, dateStrDiff } from '../timeService';

// `todayISO` returns the current TRADING day (5:15pm ET roll), not wall-clock
// today. Coach window queries default to this so "last 30 days" honors the
// session boundary — callers no longer need to remember to pass currentTradingDay.
const todayISO = () => getCurrentTradingDay();
const shiftDays = (isoDate, days) => addDaysToDateStr(isoDate, days);
const weekStartOf = (isoDate) => weekStartOfDateStr(isoDate);

// ────────────────────────────────────────────────────────────────────────────
// Flatten
// ────────────────────────────────────────────────────────────────────────────

/**
 * Flatten every session's aiInsight.observations into a single enriched
 * array, resolving playbook names in one pass.
 *
 * Sessions without an aiInsight are skipped silently — this is a derived
 * view, so "no coach output" is indistinguishable from "session doesn't
 * exist" at the archive layer.
 *
 * @param {Array}  sessions
 * @param {Object} [opts]
 * @param {Array}  [opts.playbooks]  Playbook library (for name resolution)
 * @returns {ArchivedObservation[]}
 */
export const flattenObservations = (sessions, { playbooks = [] } = {}) => {
  if (!Array.isArray(sessions)) return [];
  const playbookMap = new Map((playbooks || []).map(p => [p.id, p]));
  const out = [];
  sessions.forEach(session => {
    if (!session) return;
    const obs = session.aiInsight?.observations;
    if (!Array.isArray(obs) || obs.length === 0) return;
    const playbook = session.playbookId ? playbookMap.get(session.playbookId) : null;
    obs.forEach(o => {
      if (!o) return;
      out.push({
        ...o,
        sessionId:    session.id,
        sessionDate:  session.date || null,
        sessionName:  session.name || null,
        playbookId:   session.playbookId || null,
        playbookName: playbook?.name || null,
        generatedAt:  session.aiInsight?.generatedAt || null
      });
    });
  });
  return out;
};

// ────────────────────────────────────────────────────────────────────────────
// Query
// ────────────────────────────────────────────────────────────────────────────

/**
 * Query the archive with optional filters. All filters compose (AND).
 * Passing only `sessions` returns the full flattened corpus.
 *
 * Date semantics:
 *   - `windowDays`:  include observations whose sessionDate is within
 *                    the last N days relative to `windowAnchor` (inclusive).
 *   - `windowAnchor` defaults to the current trading day (5:15pm ET roll
 *                    via timeService). Pass an explicit anchor for
 *                    deterministic queries.
 *   - `after` / `before` are explicit 'YYYY-MM-DD' bounds (inclusive).
 *     If both `windowDays` and `after` are set, both apply (intersection).
 *
 * Playbook semantics:
 *   - `playbookId: 'none'`  → only observations from sessions with no
 *                              playbook assigned.
 *   - `playbookId: <id>`    → only observations from sessions tagged
 *                              with that playbook.
 *   - omitted               → no playbook filter.
 *
 * @param {Object} opts
 * @param {Array}  opts.sessions             Sessions corpus (required)
 * @param {Array}  [opts.playbooks]          Playbook library (for naming)
 * @param {number} [opts.windowDays]         Rolling window in days
 * @param {string} [opts.windowAnchor]       'YYYY-MM-DD' anchor for windowDays
 * @param {string} [opts.analyzer]           Exact analyzer-key match
 * @param {string} [opts.playbookId]         Playbook filter (or 'none')
 * @param {string} [opts.severity]           'success'|'info'|'warning'|'critical'
 * @param {string} [opts.category]           Category constant
 * @param {string} [opts.after]              Inclusive lower date bound
 * @param {string} [opts.before]             Inclusive upper date bound
 * @param {number} [opts.minConfidence]      Drop observations below this
 * @param {number} [opts.minPriority]        Drop observations below this
 * @returns {ArchivedObservation[]}
 */
export const queryObservations = ({
  sessions,
  playbooks,
  windowDays,
  windowAnchor,
  analyzer,
  playbookId,
  severity,
  category,
  after,
  before,
  minConfidence,
  minPriority
} = {}) => {
  const flat = flattenObservations(sessions, { playbooks });

  // Resolve rolling window to an explicit lower bound so the predicate
  // below can do cheap string compares.
  let windowLowerBound = null;
  if (typeof windowDays === 'number' && windowDays > 0) {
    const anchor = windowAnchor || todayISO();
    windowLowerBound = shiftDays(anchor, -windowDays + 1); // inclusive of anchor
  }

  return flat.filter(o => {
    const date = o.sessionDate;
    if (windowLowerBound && (!date || date < windowLowerBound)) return false;
    if (after  && (!date || date < after))  return false;
    if (before && (!date || date > before)) return false;

    if (analyzer && o.analyzer !== analyzer) return false;
    if (severity && o.severity !== severity) return false;
    if (category && o.category !== category) return false;

    if (playbookId === 'none' && o.playbookId) return false;
    if (playbookId && playbookId !== 'none' && o.playbookId !== playbookId) return false;

    if (typeof minConfidence === 'number' && (o.confidence || 0) < minConfidence) return false;
    if (typeof minPriority   === 'number' && (o.priority   || 0) < minPriority)   return false;

    return true;
  });
};

// ────────────────────────────────────────────────────────────────────────────
// Aggregation — all operate on an already-filtered corpus so callers can
// chain: queryObservations(...) → countByAnalyzer(...)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Count observations grouped by analyzer key.
 * @param {ArchivedObservation[]} observations
 * @returns {Map<string, number>}
 */
export const countByAnalyzer = (observations) => {
  const m = new Map();
  (observations || []).forEach(o => {
    const k = o.analyzer || 'unknown';
    m.set(k, (m.get(k) || 0) + 1);
  });
  return m;
};

/**
 * Count observations grouped by playbook id. Unassigned → key 'none'.
 * Emits playbook names on the returned entries so callers don't need a
 * second lookup when rendering.
 *
 * @param {ArchivedObservation[]} observations
 * @returns {Array<{ playbookId: string, playbookName: string|null, count: number }>}
 */
export const countByPlaybook = (observations) => {
  const counts = new Map();  // id → { name, count }
  (observations || []).forEach(o => {
    const id = o.playbookId || 'none';
    const name = o.playbookId ? (o.playbookName || null) : null;
    const cur = counts.get(id) || { playbookId: id, playbookName: name, count: 0 };
    cur.count += 1;
    // Prefer non-null names if we see them later (playbook added after some sessions)
    if (!cur.playbookName && name) cur.playbookName = name;
    counts.set(id, cur);
  });
  return Array.from(counts.values()).sort((a, b) => b.count - a.count);
};

/**
 * Count observations grouped by severity. Returns a fixed-shape object so
 * renderers can index into it without null-checking.
 *
 * @param {ArchivedObservation[]} observations
 * @returns {{ success: number, info: number, warning: number, critical: number }}
 */
export const countBySeverity = (observations) => {
  const out = { success: 0, info: 0, warning: 0, critical: 0 };
  (observations || []).forEach(o => {
    if (out[o.severity] !== undefined) out[o.severity] += 1;
  });
  return out;
};

/**
 * Group observations by ISO week-start (Monday). Observations missing a
 * sessionDate are dropped rather than bucketed arbitrarily.
 *
 * @param {ArchivedObservation[]} observations
 * @returns {Map<string, ArchivedObservation[]>}  key: 'YYYY-MM-DD' (Monday)
 */
export const groupByWeek = (observations) => {
  const groups = new Map();
  (observations || []).forEach(o => {
    if (!o.sessionDate) return;
    const wk = weekStartOf(o.sessionDate);
    if (!wk) return;
    if (!groups.has(wk)) groups.set(wk, []);
    groups.get(wk).push(o);
  });
  return groups;
};

/**
 * Group observations by sessionDate — useful for daily rollups.
 * @param {ArchivedObservation[]} observations
 * @returns {Map<string, ArchivedObservation[]>}
 */
export const groupByDay = (observations) => {
  const groups = new Map();
  (observations || []).forEach(o => {
    if (!o.sessionDate) return;
    if (!groups.has(o.sessionDate)) groups.set(o.sessionDate, []);
    groups.get(o.sessionDate).push(o);
  });
  return groups;
};

// ────────────────────────────────────────────────────────────────────────────
// Recurrence — answers "is this pattern recurring, emerging, or resolved?"
// Key input for per-session context badges (2B-7) and the progress scope.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Recurrence info for one analyzer across an already-filtered corpus.
 *
 * Use it for badges like "3rd time this month", or "resolved — last seen
 * 12 days ago." Trend comparison uses two equal-length windows ending at
 * `anchor`:
 *
 *   [anchor - 2*windowDays, anchor - windowDays)   ← previousWindowCount
 *   [anchor - windowDays,   anchor]                ← windowCount
 *
 * Edge cases:
 *   - No hits ever  → trend 'first-time' with all counts zero.
 *   - First-ever hit in current window (prev = 0, cur > 0) → 'first-time'.
 *   - Else if cur > prev → 'up'; cur < prev → 'down'; else 'flat'.
 *
 * @param {ArchivedObservation[]} observations    Filtered corpus
 * @param {string}                analyzerKey
 * @param {Object}  [opts]
 * @param {number}  [opts.windowDays=30]
 * @param {string}  [opts.anchor]                  'YYYY-MM-DD' (default today)
 * @returns {{
 *   total: number,
 *   firstSeen: string|null,
 *   lastSeen: string|null,
 *   windowCount: number,
 *   previousWindowCount: number,
 *   daysSinceLast: number|null,
 *   trend: 'up'|'down'|'flat'|'first-time'
 * }}
 */
export const recurrenceInfo = (observations, analyzerKey, { windowDays = 30, anchor } = {}) => {
  const hits = (observations || [])
    .filter(o => o.analyzer === analyzerKey && o.sessionDate)
    .sort((a, b) => a.sessionDate.localeCompare(b.sessionDate));

  if (hits.length === 0) {
    return {
      total: 0,
      firstSeen: null,
      lastSeen: null,
      windowCount: 0,
      previousWindowCount: 0,
      daysSinceLast: null,
      trend: 'first-time'
    };
  }

  const anchorDate = anchor || todayISO();
  const curLower  = shiftDays(anchorDate, -windowDays + 1);
  const prevLower = shiftDays(anchorDate, -windowDays * 2 + 1);
  const prevUpper = shiftDays(anchorDate, -windowDays);

  const windowCount         = hits.filter(o => o.sessionDate >= curLower  && o.sessionDate <= anchorDate).length;
  const previousWindowCount = hits.filter(o => o.sessionDate >= prevLower && o.sessionDate <= prevUpper).length;

  const lastSeen = hits[hits.length - 1].sessionDate;
  const firstSeen = hits[0].sessionDate;

  // daysSinceLast: whole days between lastSeen and anchor
  let daysSinceLast = null;
  const diff = dateStrDiff(lastSeen, anchorDate);
  if (diff != null) {
    daysSinceLast = Math.max(0, diff);
  }

  let trend;
  if (previousWindowCount === 0 && windowCount > 0) {
    // If every hit ever is inside the current window, it's genuinely new.
    trend = hits.length === windowCount ? 'first-time' : 'up';
  } else if (windowCount > previousWindowCount) trend = 'up';
  else if (windowCount < previousWindowCount) trend = 'down';
  else trend = 'flat';

  return {
    total: hits.length,
    firstSeen,
    lastSeen,
    windowCount,
    previousWindowCount,
    daysSinceLast,
    trend
  };
};

/**
 * Recurrence info for every analyzer that appears in the corpus. Handy
 * when rendering a table of "all patterns we've seen and where they are."
 *
 * @param {ArchivedObservation[]} observations
 * @param {Object}  [opts]   Forwarded to recurrenceInfo
 * @returns {Array<{ analyzer: string, info: ReturnType<typeof recurrenceInfo> }>}
 *          sorted by windowCount desc, then total desc.
 */
export const recurrenceAll = (observations, opts = {}) => {
  const keys = new Set();
  (observations || []).forEach(o => {
    if (o.analyzer) keys.add(o.analyzer);
  });
  const rows = Array.from(keys).map(analyzer => ({
    analyzer,
    info: recurrenceInfo(observations, analyzer, opts)
  }));
  rows.sort((a, b) => {
    if (b.info.windowCount !== a.info.windowCount) return b.info.windowCount - a.info.windowCount;
    return b.info.total - a.info.total;
  });
  return rows;
};

// ────────────────────────────────────────────────────────────────────────────
// Convenience
// ────────────────────────────────────────────────────────────────────────────

/**
 * How many distinct sessions exist in the corpus (useful for denominators
 * like "fired in 7 of the last 20 sessions"). Does not require an aiInsight.
 *
 * @param {Array}  sessions
 * @param {Object} [opts]
 * @param {number} [opts.windowDays]
 * @param {string} [opts.windowAnchor]
 * @param {string} [opts.playbookId]      'none' for unassigned, or an id
 * @returns {number}
 */
/**
 * Count sessions matching the filters.
 *
 * Supports two windowing modes:
 *   1. Rolling: `windowDays` + optional `windowAnchor` → counts the last
 *      `windowDays` days ending at the anchor.
 *   2. Explicit bounds: `after` (inclusive) + `before` (inclusive) — useful
 *      for computing prior-window comparisons in trend analyzers.
 *      Either bound may be omitted; both can be combined with `windowDays`
 *      (intersection wins).
 */
export const countSessions = (sessions, { windowDays, windowAnchor, playbookId, after, before } = {}) => {
  if (!Array.isArray(sessions)) return 0;
  let windowLowerBound = null;
  if (typeof windowDays === 'number' && windowDays > 0) {
    const anchor = windowAnchor || todayISO();
    windowLowerBound = shiftDays(anchor, -windowDays + 1);
  }
  return sessions.filter(s => {
    if (!s) return false;
    if (windowLowerBound && (!s.date || s.date < windowLowerBound)) return false;
    if (after && (!s.date || s.date < after)) return false;
    if (before && (!s.date || s.date > before)) return false;
    if (playbookId === 'none' && s.playbookId) return false;
    if (playbookId && playbookId !== 'none' && s.playbookId !== playbookId) return false;
    return true;
  }).length;
};
